import { ProcessedRoute } from './types';
import { useLanguage } from '../../../contexts/language';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

export interface DeleteRouteConfirmationDialogProps {
  contractId: string;
  route: ProcessedRoute | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteRouteConfirmationDialog = ({
  isLoading,
  onClose,
  onConfirm,
  contractId,
  route,
}: DeleteRouteConfirmationDialogProps) => {
  const { t } = useLanguage();

  const { deleteContractRoute, getContractRouteDeletionImpactedJourneys } =
    useIotsApi();

  const { data: affectedJourneys = 0, isFetching: isFetchingAffectedJourneys } =
    useQuery({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ['route-affected-journeys', contractId, route?.id ?? ''],
      queryFn: () =>
        route
          ? getContractRouteDeletionImpactedJourneys(contractId, route.id)
          : Promise.resolve(0),
    });

  const deleteRouteMutation = useMutation({
    mutationFn: (routeId: string) => deleteContractRoute(contractId, routeId),
  });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  return (
    <ConfirmationDialog
      open={Boolean(route)}
      onClose={onClose}
      onConfirm={() => {
        if (route) {
          deleteRouteMutation.mutate(route.id, {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.contract-route-deletion-success'),
              });
              onConfirm();
              onClose();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.contract-route-deletion-error'),
                ),
              });
              deleteRouteMutation.reset();
            },
          });
        }
      }}
      loading={
        isLoading || deleteRouteMutation.isPending || isFetchingAffectedJourneys
      }
      title={t('content.contract-route-deletion-confirmation.title')}
    >
      <ConsequencesList
        action={t('content.contract-route-deletion-confirmation.action')}
        list={[
          t(
            'content.contract-route-deletion-confirmation.bullet-points.will-be-visually-removed',
          ),
          t(
            'content.contract-route-deletion-confirmation.bullet-points.placeholders-will-be-removed',
          ),
          t(
            'content.contract-route-deletion-confirmation.bullet-points.an-amount-of-journeys-will-become-adhoc',
            {
              AMOUNT: isFetchingAffectedJourneys
                ? '...'
                : affectedJourneys.toString(),
            },
          ),
        ]}
      />
    </ConfirmationDialog>
  );
};

export default DeleteRouteConfirmationDialog;
